import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import colours from 'theme/atoms/colours';

import * as RechartsStyled from 'dashboard/styles/Recharts.style';
import * as Styled from 'dashboard/styles/Overview.style';
import * as Text from 'theme/components/Text.style';
import { useRiskStatistics } from 'dashboard/hooks/useRiskStatistics';
import riskChartItems from 'dashboard/helpers/riskChartItems';
import { isTouchDevice } from 'shared/helpers/device';
import {
  BarShape,
  CustomCursor,
} from 'dashboard/components/HorizontalBarShapes';
import CustomTooltip from './CustomTooltip';
import EmptyState from './EmptyState';

const RISK_COLORS = [
  '#0088FF',
  '#FFCC00',
  '#FF383C',
  '#CB30E0',
  '#AC7F5E',
  '#00C0E8',
  '#00C8B3',
  '#FF8D28',
  '#6155F5',
  '#FF2D55',
  '#34C759',
];

const BORDER_PADDING = 10;
const X_AXIS_HEIGHT = 30;
interface RiskSectionProps {
  queryParams: string;
}

export default function RiskSection({ queryParams }: RiskSectionProps) {
  const { t } = useTranslation('pageDashboard');
  const { data, isSuccess } = useRiskStatistics(queryParams);

  const chartItems = useMemo(
    () => riskChartItems(data ?? [], RISK_COLORS),
    [data]
  );
  const isEmpty = chartItems.length === 0;

  const chartHeight = Math.max(200, chartItems.length * 40);

  const chartStyle = {
    fontFamily: 'Inter, sans-serif',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  } as const;

  const renderChart = () => (
    <RechartsStyled.HorizontalBarLayout>
      <RechartsStyled.HorizontalBarLabels
        $paddingTop={BORDER_PADDING}
        $paddingBottom={BORDER_PADDING}
      >
        {chartItems.map((item) => (
          <RechartsStyled.HorizontalBarLabel key={item.risk_key}>
            {item.risk.replace(/ Risk$/i, '')}
          </RechartsStyled.HorizontalBarLabel>
        ))}
      </RechartsStyled.HorizontalBarLabels>

      <RechartsStyled.ChartBorder>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartItems}
            layout="vertical"
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            style={chartStyle}
          >
            <YAxis type="category" dataKey="risk" hide />
            <XAxis type="number" allowDecimals={false} hide />
            <CartesianGrid horizontal={false} stroke={colours.neutral[100]} />
            <Tooltip
              trigger={isTouchDevice() ? 'click' : 'hover'}
              content={
                <CustomTooltip
                  occurrencesLabel={(count) => t('risk.occurrences', { count })}
                />
              }
              cursor={<CustomCursor />}
            />
            <Bar dataKey="note_count" barSize={10} shape={<BarShape />}>
              {chartItems.map((item) => (
                <Cell key={item.risk_key} fill={item.colour} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </RechartsStyled.ChartBorder>

      <div style={{ gridColumn: 2, padding: `0 ${BORDER_PADDING + 1}px` }}>
        <ResponsiveContainer width="100%" height={X_AXIS_HEIGHT}>
          <BarChart data={chartItems} layout="vertical" style={chartStyle}>
            <YAxis type="category" dataKey="risk" hide />
            <XAxis
              type="number"
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tick={{
                fill: colours.neutral[900],
                fontSize: 13,
                fontWeight: 650,
              }}
              padding={{ right: 15 }}
            />
            <Bar dataKey="note_count" fill="transparent" barSize={0} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </RechartsStyled.HorizontalBarLayout>
  );

  if (!isSuccess) return null;

  return (
    <Styled.SectionContainer>
      <Text.H5>{t('risk.title')}</Text.H5>

      <Styled.Card>
        <RechartsStyled.ChartContainer>
          {isEmpty ? <EmptyState labels={data ?? []} /> : renderChart()}
        </RechartsStyled.ChartContainer>
      </Styled.Card>
    </Styled.SectionContainer>
  );
}
